import { IdentityUserAri } from '@atlassian/ari/identity/user';
import { JiraNavigationSettingsAri } from '@atlassian/ari/jira/navigation-settings';
import { JiraWorkspaceAri } from '@atlassian/ari/jira/workspace';
import {
	buildNavigationAriForMenuData,
	buildNavigationAriForSidebarData,
} from '@atlassian/global-side-navigation/navigation-ari';
import { UNSAFE_noExposureExp } from '@atlassian/jira-feature-experiments';
import { fg } from '@atlassian/jira-feature-gating';
import type {
	JiraSpaRoute,
	RouterContext,
} from '@atlassian/react-resource-router/src/common/types.tsx';
import type { GSNQueryPreloaderQuery$variables } from '@jira/nav-platform-integration__sidebar/src/__generated__/GSNQueryPreloaderQuery.graphql.ts';
import {
	MENU_ID_APPS,
	MENU_ID_FILTERS,
	MENU_ID_OPERATIONS,
	MENU_ID_PLANS,
	MENU_ID_PROJECTS,
} from '@jira/navigation-apps__sidebar-nav4-context/src/common/constants.tsx';
import { getInitialSelectedPath } from '@jira/navigation-apps__sidebar-nav4-context/src/common/utils/selected-path/getInitialSelectedPath.tsx';
import { isSelectedPath } from '@jira/navigation-apps__sidebar-nav4-context/src/common/utils/selected-path/isSelectedPath.tsx';
import { PREMIUM_EDITION } from '@jira/platform__application-editions/src/edition.tsx';
import type { ApplicationPermissions } from '@jira/platform__tenant-context-types/src/tenant-context.tsx';

/**
 * Derives the Relay operation variables Jira's global-side-navigation fragments
 * need.
 *
 * Shared so one derivation feeds both `navPlatformSidebarResource` (the
 * standalone GSN query) and the GlobalRoot query (`useJiraAppChromeQuery`),
 * which spreads `...GSNForContentSidebarEntryPoint` and therefore needs the same
 * values as root operation variables. This mirrors `getTopNavRelayVariables`,
 * which does the same job for `...TopNavigation_slotsQuery`.
 *
 * Keeping it in one place is the point: several of these are gate- or
 * experiment-conditional, and two copies would drift silently — the query would
 * still compile, it would just fetch different data on one path than the other.
 */
export const getSideNavRelayVariables = (
	routerContext: RouterContext<JiraSpaRoute>,
	{
		activationId,
		appEditions,
		appPermissions,
		atlassianAccountId,
		cloudId,
	}: {
		activationId: string;
		appEditions: { serviceDesk: string };
		appPermissions: ApplicationPermissions;
		atlassianAccountId: string | null;
		cloudId: string;
	},
): GSNQueryPreloaderQuery$variables => {
	const initialSelectedPath = getInitialSelectedPath(routerContext);
	const ownerAri =
		atlassianAccountId && IdentityUserAri.create({ userId: atlassianAccountId }).toString();
	const isItamEnabled = fg('itam_endpoint_access')
		? appPermissions.hasServiceDeskAccess &&
			appEditions.serviceDesk === PREMIUM_EDITION &&
			fg('itam_ftw')
		: fg('itam_ftw');

	// Fetch the productionisation recommendations (`growthRecommendations`
	// behind `@include(if: $shouldFetchInvitedAppShortcuts)`) only for the test
	// cohort. Read via `UNSAFE_noExposureExp` so control skips the field without
	// firing an exposure here — exposure fires at render once recs show.
	const shouldFetchInvitedAppShortcuts =
		fg('twcg-460-preload-grs-invited-app-shortcuts-gate') &&
		UNSAFE_noExposureExp('twcg-460-preload-grs-invited-app-shortcuts')[0].get('isEnabled', false);

	// Fetch the stored "keep the JSM shortcut" flag (`keepJsmShortcutProperty` behind
	// `@include(if: $shouldFetchKeepJsmShortcut)`) only for the jsmgrowth-495 test group:
	// control never shows the shortcut.
	// Read the experiment via `UNSAFE_noExposureExp` to decide this WITHOUT firing an exposure here:
	// the exposure fires in `useShowJsmShortcut` at render, only for users who are eligible for the
	// shortcut, so it stays limited to the users GRS has invited to JSM.
	const hasJiraAndJsmAccess =
		appPermissions.hasServiceDeskAccess &&
		(appPermissions.hasCoreAccess || appPermissions.hasSoftwareAccess);
	const shouldFetchKeepJsmShortcut =
		hasJiraAndJsmAccess && UNSAFE_noExposureExp('jsmgrowth-495')[0].get('isEnabled', false);
	let shouldFetchItamStaticChecks = false;
	let itamStaticChecks: Array<{ readonly resourceId: string; readonly permissionId: string }> = [];
	if (isItamEnabled && fg('prefetch_ham_permissions_in_sidebar')) {
		shouldFetchItamStaticChecks = true;
		itamStaticChecks = [
			{
				resourceId: `ari:cloud:jira-servicedesk::site/${cloudId}`,
				permissionId: 'write',
			},
			{
				resourceId: `ari:cloud:jira::site/${cloudId}`,
				permissionId: 'manage',
			},
			{
				resourceId: `ari:cloud:cmdb::site/${cloudId}`,
				permissionId: 'manage',
			},
			{ resourceId: '', permissionId: 'manage-ham' },
			{ resourceId: '', permissionId: 'view-ham' },
		];
	} else if (isItamEnabled && !fg('itam_nav4_unify_permission_checks')) {
		shouldFetchItamStaticChecks = true;
		itamStaticChecks = [
			{
				resourceId: `ari:cloud:jira-servicedesk::site/${cloudId}`,
				permissionId: 'write',
			},
			{
				resourceId: `ari:cloud:jira::site/${cloudId}`,
				permissionId: 'manage',
			},
			{
				resourceId: `ari:cloud:cmdb::site/${cloudId}`,
				permissionId: 'manage',
			},
		];
	}

	return {
		cloudId,
		entityAriApps: JiraNavigationSettingsAri.create({
			siteId: cloudId,
			activationId,
			navigationSettingScope: 'apps',
		}).toString(),
		entityAriFilters: JiraNavigationSettingsAri.create({
			siteId: cloudId,
			activationId,
			navigationSettingScope: 'filters',
		}).toString(),
		entityAriPlans: JiraNavigationSettingsAri.create({
			siteId: cloudId,
			activationId,
			navigationSettingScope: 'plans',
		}).toString(),
		...(fg('jpd-enable-roadmaps-recents-fe')
			? {
					entityAriRoadmaps: JiraNavigationSettingsAri.create({
						siteId: cloudId,
						activationId,
						navigationSettingScope: 'collections',
					}).toString(),
				}
			: {}),
		entityAriUserSideNavSettings: JiraWorkspaceAri.create({
			siteId: cloudId,
			activationId,
		}).toString(),
		isAppsExpanded: isSelectedPath(initialSelectedPath, MENU_ID_APPS),
		isFiltersExpanded: isSelectedPath(initialSelectedPath, MENU_ID_FILTERS),
		isPlansExpanded: isSelectedPath(initialSelectedPath, MENU_ID_PLANS),
		isProjectsExpanded: isSelectedPath(initialSelectedPath, MENU_ID_PROJECTS),
		isOperationsExpanded: isSelectedPath(initialSelectedPath, MENU_ID_OPERATIONS),
		navPlatMenuDataEntityAri: buildNavigationAriForMenuData({
			activationId,
			firstPartyApp: 'jira',
			siteId: cloudId,
		}),
		navPlatSidebarDataEntityAri: buildNavigationAriForSidebarData({
			activationId,
			firstPartyApp: 'jira',
			siteId: cloudId,
		}),
		ownerAri,
		fetchGlobalShortcutsAvailableProducts: Boolean(cloudId && ownerAri),
		isItamEnabled,
		shouldFetchItamStaticChecks,
		shouldFetchItamVerticalInstantiations: isItamEnabled && !fg('hide_itam_settings_in_sidebar'),
		itamStaticChecks,
		shouldFetchInvitedAppShortcuts,
		invitedAppShortcutsProduct: 'jira',
		shouldFetchKeepJsmShortcut,
	};
};
